import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Dialog from '../../organisms/Dialog';
import { collateralApi } from '../../../services/collateralApi';
import { formatCurrency } from './collateralUtils';

interface Props {
    analysisId: string;
    operationCode: string;
    offerOperationId?: string;
    onClose: () => void;
    onSuccess: () => void;
}

const ReceivableModalForm = ({ analysisId, operationCode, offerOperationId, onClose, onSuccess }: Props) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [operationData, setOperationData] = useState<Record<string, unknown> | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const data = await collateralApi.getOperationData(operationCode, offerOperationId);
                setOperationData(data);
            } catch { setError('Erro ao buscar dados da operação'); }
            finally { setLoading(false); }
        })();
    }, [operationCode, offerOperationId]);

    const documents = (operationData?.['documents'] || []) as Record<string, unknown>[];
    const totalReceivables = documents.reduce((sum: number, d: Record<string, unknown>) => sum + ((d.receivables as unknown[])?.length || 0), 0);
    const totalAmount = documents.reduce((sum: number, d: Record<string, unknown>) =>
        sum + ((d.receivables || []) as Record<string, unknown>[]).reduce((s: number, r: Record<string, unknown>) => s + ((r.amount as number) || 0), 0), 0);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await collateralApi.populateReceivables(analysisId, documents);
            onSuccess();
        } catch {
            toast.error('Erro ao carregar recebíveis');
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            id="load-receivables"
            show={true}
            onClose={onClose}
            header="Carregar Recebíveis"
            size="sm"
            body={
                <div className="p-5 min-w-[350px]">
                    {loading ? (
                        <div className="animate-pulse space-y-3">
                            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
                            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
                            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-2/3" />
                        </div>
                    ) : error ? (
                        <p className="text-red-500 text-sm">{error}</p>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Operação: <strong className="dark:text-white">{operationCode}</strong></p>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Documentos (NFe)</span>
                                <span className="font-medium dark:text-white">{documents.length}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Total de recebíveis</span>
                                <span className="font-medium dark:text-white">{totalReceivables}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">Valor total</span>
                                <span className="font-medium dark:text-white">{formatCurrency(totalAmount)}</span>
                            </div>
                        </div>
                    )}
                </div>
            }
            footer={
                <div className="flex gap-3 justify-end w-full">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50">
                        Cancelar
                    </button>
                    <button onClick={handleConfirm} disabled={loading || !!error || submitting}
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        {submitting ? 'Carregando...' : 'Confirmar'}
                    </button>
                </div>
            }
        />
    );
};

export default ReceivableModalForm;
